import React, { useMemo, useState } from 'react';
import { Alert, Button, Progress, Statistic, Typography, message } from 'antd';
import { useAIBuilderApi } from '../../../api/aibuilder/aibuilder';
import { TDataset, TMLModel } from '../../../models/aibuilder/aibuilder';
import { ImageModel } from '../engine/useImageModel';
import { TextModel } from '../engine/useTextModel';
import { buildTrainingSamples } from '../engine/buildTrainingSamples';
import { buildTextTrainingSamples } from '../engine/buildTextTrainingSamples';
import { runImageSelfCheck, runTextSelfCheck } from '../engine/modelSelfCheck';
import DatasetDiagnosticsPanel from './DatasetDiagnosticsPanel';

export interface TrainStationProps {
  dataset: TDataset;
  imageModel: ImageModel;
  textModel: TextModel;
  purpose: string;
  editable: boolean;
  onChanged?: (model?: TMLModel) => void;
}

const TrainStation: React.FC<TrainStationProps> = ({
  dataset,
  imageModel,
  textModel,
  purpose,
  editable,
  onChanged,
}) => {
  const { createModel } = useAIBuilderApi();
  const [success, setSuccess] = useState(false);
  const [selfCheckIssues, setSelfCheckIssues] = useState<string[]>([]);
  const [selfChecking, setSelfChecking] = useState(false);
  const isText = dataset.data_type === 'text';
  const activeModel = isText ? textModel : imageModel;
  const labels = useMemo(() => dataset.labels || [], [dataset.labels]);
  const samples = useMemo(() => dataset.samples || [], [dataset.samples]);
  const ready = useMemo(
    () =>
      labels.length >= 2 &&
      labels.every((label) =>
        samples.some(
          (sample) =>
            !sample.excluded &&
            sample.label_id === label.id &&
            (isText ? sample.content_text : sample.content_url),
        ),
      ),
    [labels, samples, isText],
  );

  const train = async () => {
    try {
      setSuccess(false);
      setSelfCheckIssues([]);
      const result = isText
        ? await textModel.train(buildTextTrainingSamples(dataset))
        : await imageModel.train(buildTrainingSamples(dataset));
      const response = await createModel(dataset.id, {
        accuracy: result.accuracy,
        purpose: purpose || undefined,
      });
      setSuccess(true);
      onChanged?.(response.data);
      setSelfChecking(true);
      try {
        const checkIssues = isText
          ? await runTextSelfCheck(dataset, textModel)
          : await runImageSelfCheck(dataset, imageModel);
        setSelfCheckIssues(checkIssues);
      } finally {
        setSelfChecking(false);
      }
    } catch (error: any) {
      message.error(error?.response?.data || error?.message || 'Échec de l’entraînement.');
    }
  };

  return (
    <div>
      <Alert
        type={ready ? 'info' : 'warning'}
        showIcon
        message={
          ready
            ? 'Le dataset est prêt.'
            : `Ajoutez au moins deux labels, avec une ${isText ? 'phrase' : 'image'} pour chacun.`
        }
        style={{ marginBottom: 16 }}
      />
      <DatasetDiagnosticsPanel dataset={dataset} accuracy={activeModel.accuracy} extraIssues={selfCheckIssues} />
      {selfChecking && (
        <Typography.Text type="secondary">Vérification de tes exemples…</Typography.Text>
      )}
      <Button
        type="primary"
        onClick={train}
        loading={activeModel.status === 'loading' || activeModel.status === 'training'}
        disabled={!ready || !editable}
      >
        Entraîner
      </Button>
      {(activeModel.status === 'loading' || activeModel.status === 'training') && (
        <Progress percent={activeModel.progress} status="active" />
      )}
      {activeModel.accuracy !== null && activeModel.status === 'ready' && (
        <>
          <Statistic title="Précision" value={activeModel.accuracy} suffix="%" />
          {activeModel.evalCount !== null && (
            <Typography.Text type="secondary">
              Calculée sur {activeModel.evalCount} {isText ? 'phrase' : 'image'}
              {activeModel.evalCount > 1 ? 's' : ''} de test.
            </Typography.Text>
          )}
        </>
      )}
      {success && <Alert type="success" showIcon message="Modèle enregistré." style={{ marginTop: 16 }} />}
    </div>
  );
};

export default TrainStation;
